<template>
  <div class="redirection-details">
    <div class="redirection-content pa-4">
      <div class="label font-weight-bold pb-1">{{ $t('scan_destination_title') }}</div>
      <div class="hint">{{ $t('scan_destination_hint') }}</div>

      <div v-if="loading" class="loading-ctn">
        <v-progress-circular indeterminate color="primary" size="32" />
      </div>

      <!-- destination cards -->
      <destination-cards
        v-if="!loading"
        class="mt-6"
        :value="destinationType"
        @input="selectDestination"
      >
        <template v-if="destinationType === 'dpp' && isUsingDpp && !isModified" v-slot:dpp-action>
          <v-btn class="mt-1" small color="primary" text @click.stop="openDppEditor">
            <v-icon left small>mdi-open-in-new</v-icon>{{ $t('open_dpp_editor') }}
          </v-btn>
        </template>
        <template v-if="destinationType === 'custom' && isUsingCustomPage && !isModified" v-slot:custom-action>
          <v-btn class="mt-1" small color="primary" text @click.stop="openLandingPageEditor">
            <v-icon left small>mdi-open-in-new</v-icon>{{ $t('open_editor') }}
          </v-btn>
        </template>
      </destination-cards>

      <!-- Only surfaced for products still on the legacy DPP layout, so they can move
           off it. Once saved as a showcase there is nothing left to choose between. -->
      <div v-if="!loading && destinationType === 'custom' && showLegacyDppLayout" class="layout-section mt-6">
        <div class="section-label">{{ $t('start_from_a_layout') }}</div>
        <div class="layout-cards mt-3">
          <destination-option-card
            orientation="vertical"
            icon="mdi-image"
            :title="$t('product_showcase')"
            :description="$t('product_showcase_desc')"
            :selected="layout === 'showcase'"
            @select="selectLayout('showcase')"
          />

          <destination-option-card
            orientation="vertical"
            icon="mdi-file-document"
            :title="$t('dpp_page_template')"
            :description="$t('digital_product_passport_desc')"
            :selected="layout === 'dpp'"
            @select="selectLayout('dpp')"
          />
        </div>
      </div>

      <div v-if="!loading && destinationType === 'existing'" class="existing-page-select my-6">
        <v-autocomplete
          ref="existingSelect"
          v-model="form.stc_config"
          :items="selectableLandingPages"
          :loading="landingPagesLoading"
          :search-input.sync="landingPageSearch"
          :no-filter="true"
          :label="$t('select_landing_page')"
          :no-data-text="$t('no_data_available')"
          :menu-props="{ offsetY: true }"
          :rules="rules.stc_config"
          item-value="id"
          item-text="name"
          outlined
          dense
          hide-details="auto"
          clearable
        >
          <template v-slot:item="{ item }">
            <div class="d-flex align-center" style="width: 100%">
              <span class="flex-grow-1 text-truncate">{{ item.name }}</span>
              <span class="page-tag">{{ landingPageLayoutTag(item) }}</span>
            </div>
          </template>
          <template v-slot:selection="{ item }">
            <span class="text-truncate">{{ item.name }} - {{ landingPageLayoutTag(item) }}</span>
          </template>
        </v-autocomplete>
        <div v-if="isBorrowedLandingPage" class="borrowed-note">
          <v-icon size="15" class="mr-1">mdi-information-outline</v-icon>
          <span>{{ $t('landing_page_managed_in_another_product') }}</span>
        </div>
      </div>

      <div v-if="!loading && destinationType === 'redirect'" class="mt-6">
        <v-form ref="urlForm" @submit.prevent>
          <v-text-field
            v-model="form.url"
            class="form-input"
            :label="$t('URL')"
            :rules="rules.url"
            validate-on-blur
            outlined
            dense
          />
        </v-form>
      </div>
    </div>

    <div v-if="!loading" class="actions-ctn">
      <v-btn
        class="save-btn px-6"
        color="primary"
        raised
        :disabled="!isModified || saving"
        :loading="saving"
        @click="save()"
      >{{ $t('save') }}</v-btn>
      <span v-if="['custom', 'dpp'].includes(destinationType) && isModified" class="save-hint">
        <v-icon size="16" class="mr-1">mdi-information-outline</v-icon>{{ destinationType === 'dpp' ? $t('save_destination_to_open_dpp_editor') : $t('save_destination_to_open_editor') }}
      </span>
    </div>

    <confirmation-dialog
      :display-confirmation-dialog.sync="showReplaceWarning"
      :title="$t('replace_landing_page_title')"
      :content="$t('replace_landing_page_warning')"
      @handle-confirmation="onReplaceConfirm"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import DestinationOptionCard from '@/components/redirection/DestinationOptionCard.vue'
import DestinationCards from '@/components/redirection/DestinationCards.vue'
import DefaultLandingPageTemplate from '@/const/templates/default_landing_page'
import DppLandingPageTemplate from '@/const/templates/dpp_landing_page'
import ConfirmationDialog from '@/components/profile/dialog/ConfirmationDialog.vue'
import Utils from '@/utils/utils'
import { buildDestinationPayload, urlRules } from '@/utils/destination'

export default {
  name: 'ProductDetailsRedirection',
  components: { DestinationOptionCard, DestinationCards, ConfirmationDialog },
  computed: {
    ...mapGetters(['defaultRedirectionRule', 'isDefaultRedirectionRuleLoaded', 'landingPages', 'selectedProduct', 'profile']),

    isModified () {
      return JSON.stringify(this.currentState) !== JSON.stringify(this.savedState)
    },
    currentState () {
      return {
        destinationType: this.destinationType,
        layout: this.destinationType === 'custom' ? this.layout : null,
        url: this.destinationType === 'redirect' ? (this.form.url || '') : '',
        stc_config: this.destinationType === 'existing' ? (this.form.stc_config || null) : null
      }
    },
    isBorrowedLandingPage () {
      return this.destinationType === 'existing' && this.form.stc_config != null && this.form.stc_config !== this.customPageId
    },
    // The DPP page template is a legacy layout being phased out, so the layout row
    // is shown only while a product is still on it. The saved-config check keeps the
    // row up after switching the selection to showcase (so the choice stays
    // reversible until saved); once saved, the row is gone for good.
    showLegacyDppLayout () {
      return this.layout === 'dpp' || this.layoutFromConfig(this.customPageConfig) === 'dpp'
    },
    selectableLandingPages () {
      // The product's custom page is offered via the Custom card, so keep it out of the "use existing" list.
      return this.existingLandingPages.filter(page => page.id !== this.customPageId)
    }
  },
  data () {
    return {
      loading: true,
      saving: false,
      landingPagesLoading: false,
      existingLandingPages: [],
      landingPageSearch: null,
      debounceSearch: null,
      destinationType: 'custom',
      layout: 'showcase', // 'showcase' | 'dpp'
      form: { url: '', stc_config: null },
      destinationId: null,
      customPageId: null,
      customPageConfig: null,
      isUsingCustomPage: false,
      isUsingDpp: false,
      showReplaceWarning: false,
      replaceResolve: null,
      savedState: null, // isModified compares against this
      rules: {
        url: urlRules(this.$t.bind(this)),
        stc_config: [
          v => !!v || this.$t('field_required')
        ]
      }
    }
  },
  watch: {
    isModified (val) { this.setUnsavedChangesStatus(val) },
    landingPageSearch () {
      if (this.debounceSearch) this.debounceSearch()
    },
    showReplaceWarning (val) {
      if (!val && this.replaceResolve) { this.replaceResolve(false); this.replaceResolve = null }
    }
  },
  methods: {
    ...mapMutations({ setUnsavedChangesStatus: 'SET_UNSAVED_CHANGES_STATUS' }),

    templateForLayout (layout) {
      return layout === 'dpp' ? DppLandingPageTemplate : DefaultLandingPageTemplate
    },
    layoutFromConfig (configJson) {
      return configJson?.editor?.type === 'dpp' ? 'dpp' : 'showcase'
    },
    isDpp (item) {
      return this.layoutFromConfig(item?.config_json) === 'dpp'
    },
    landingPageLayoutTag (item) {
      return this.isDpp(item) ? this.$t('dpp') : this.$t('showcase')
    },
    selectDestination (type) {
      this.destinationType = type
      if (type === 'redirect' && !this.form.url) {
        this.form.url = this.profile?.company?.website || ''
      }
      if (type === 'existing' && !this.existingLandingPages.length) {
        this.loadPages()
      }
    },
    selectLayout (layout) {
      this.layout = layout
    },
    async loadPages () {
      this.landingPagesLoading = true
      await this.$store.dispatch('loadLandingPages')
      this.existingLandingPages = this.landingPages.slice()
      this.landingPagesLoading = false
    },
    async runLandingPageSearch () {
      this.landingPagesLoading = true
      const searchText = this.landingPageSearch || ''
      const res = await this.$store.dispatch('searchLandingPages', { search: searchText }).catch(() => null)
      if (searchText !== (this.landingPageSearch || '')) return
      if (res) this.existingLandingPages = res
      this.landingPagesLoading = false
    },
    openLandingPageEditor () {
      const id = this.customPageId
      if (!id) return
      this.$router.push({ name: 'landing-page.edit', params: { id }, query: { product: this.selectedProduct.id, from: 'product-details' } })
    },
    openDppEditor () {
      this.$router.push({ name: 'products.dpp', params: { id: this.selectedProduct.id } })
    },
    async prepareCustomPage () {
      // Create the page if missing, or overwrite it when switching layout. Returns false if the user cancels the replace warning.
      if (!this.customPageId) {
        await this.createCustomPage(this.templateForLayout(this.layout))
        return true
      }
      const currentLayout = this.layoutFromConfig(this.customPageConfig)
      if (currentLayout === this.layout) return true
      if (this.customPageDiffersFromDefault(currentLayout) && !(await this.confirmReplace())) return false
      await this.overwriteCustomPage(this.templateForLayout(this.layout))
      return true
    },
    async createCustomPage (template) {
      const id = await this.$store.dispatch('createLandingPage', { name: this.selectedProduct.name, configJson: template })
      await this.$store.dispatch('updateProductJson', { id: this.selectedProduct.id, json: { scantrust_landing_page: id } })
      this.customPageId = id
      this.customPageConfig = template
    },
    async overwriteCustomPage (template) {
      await this.$store.dispatch('updateLandingPage', { id: this.customPageId, configJson: template })
      this.customPageConfig = template
    },
    // Order-insensitive JSON: the backend re-serializes config_json key order
    // (jsonb) on save+reload, so a plain stringify compare would flag an
    // untouched page as edited. Sorting keys first compares content, not order.
    sortedJson (value) {
      if (Array.isArray(value)) return '[' + value.map(item => this.sortedJson(item)).join(',') + ']'
      if (value && typeof value === 'object') {
        return '{' + Object.keys(value).sort().map(key => JSON.stringify(key) + ':' + this.sortedJson(value[key])).join(',') + '}'
      }
      return JSON.stringify(value)
    },
    customPageDiffersFromDefault (currentLayout) {
      return this.sortedJson(this.customPageConfig?.editor) !== this.sortedJson(this.templateForLayout(currentLayout).editor)
    },
    confirmReplace () {
      return new Promise(resolve => {
        this.replaceResolve = resolve
        this.showReplaceWarning = true
      })
    },
    onReplaceConfirm () {
      if (this.replaceResolve) { this.replaceResolve(true); this.replaceResolve = null }
      this.showReplaceWarning = false
    },
    buildDestinationPayload () {
      return buildDestinationPayload({
        destinationType: this.destinationType,
        url: this.form.url,
        stcConfig: this.form.stc_config,
        customPageId: this.customPageId
      })
    },
    async save () {
      if (this.destinationType === 'redirect' && !this.$refs.urlForm.validate()) return
      if (this.destinationType === 'existing' && !this.$refs.existingSelect.validate(true)) return
      if (this.destinationType === 'custom' && !(await this.prepareCustomPage())) return
      this.saving = true
      try {
        if (this.destinationId) {
          const payload = this.buildDestinationPayload()
          await this.$store.dispatch('updateRuleDestination', { ruleId: this.defaultRedirectionRule.id, destination: { ...payload, id: this.destinationId } })
        }
        this.captureSavedState()
        this.isUsingCustomPage = this.destinationType === 'custom'
        this.isUsingDpp = this.destinationType === 'dpp'
        this.setUnsavedChangesStatus(false)
        this.$store.dispatch('showSnackBar', { message: this.$t('saved_successfully') })
      } finally {
        this.saving = false
      }
    },
    captureSavedState () {
      this.savedState = JSON.parse(JSON.stringify(this.currentState))
    },
    restoreSavedState () {
      if (!this.savedState) return
      this.destinationType = this.savedState.destinationType
      this.layout = this.savedState.layout || 'showcase'
      this.form.url = this.savedState.url || ''
      this.form.stc_config = this.savedState.stc_config
    },
    onDiscardUnsavedChanges () {
      this.restoreSavedState()
    },
    applySavedDestination (destination) {
      this.destinationId = destination?.id || null
      if (!destination || !destination.type) { this.destinationType = 'custom'; this.layout = this.layoutFromConfig(this.customPageConfig); return }
      if (destination.type === 'url') {
        this.destinationType = 'redirect'
        this.form.url = destination.url || ''
      } else if (destination.type === 'dpp') {
        this.destinationType = 'dpp'
        this.isUsingDpp = true
      } else if (destination.type === 'stc') {
        if (destination.stc_config && destination.stc_config === this.customPageId) {
          this.destinationType = 'custom'
          this.layout = this.layoutFromConfig(this.customPageConfig)
          this.isUsingCustomPage = true
        } else {
          this.destinationType = 'existing'
          this.form.stc_config = destination.stc_config || null
        }
      }
    }
  },
  async created () {
    this.debounceSearch = Utils.debounce(() => this.runLandingPageSearch(), 400)
    try {
      if (!this.isDefaultRedirectionRuleLoaded) {
        await this.$store.dispatch('loadDefaultRedirectionRule')
      }
      await this.loadPages().catch(() => {})

      this.customPageId = this.selectedProduct?.stc_data?.scantrust_landing_page || null
      if (!this.customPageId && this.selectedProduct?.name) {
        // Legacy products can lack a stored custom page id, so search by matching name
        const matchingPages = await this.$store.dispatch('searchLandingPages', { search: this.selectedProduct.name }).catch(() => [])
        const customPage = (matchingPages || []).find(page => page && page.name === this.selectedProduct.name)
        this.customPageId = customPage ? customPage.id : null
      }

      if (this.customPageId) {
        const loadedCustomPage = await this.$store.dispatch('loadLandingPage', { id: this.customPageId }).catch(() => null)
        this.customPageConfig = loadedCustomPage ? loadedCustomPage.config_json : null
      }

      const destId = this.selectedProduct?.stc_data?.scantrust_destination
      if (destId) {
        const destination = await this.$store.dispatch('loadDestination', { ruleId: this.defaultRedirectionRule.id, destinationId: destId })
        this.applySavedDestination(destination)
      }

      // Make sure a pre-selected existing page is in the dropdown list
      if (this.destinationType === 'existing' && this.form.stc_config && !this.existingLandingPages.some(page => page.id === this.form.stc_config)) {
        const selectedPage = await this.$store.dispatch('loadLandingPage', { id: this.form.stc_config }).catch(() => null)
        if (selectedPage) this.existingLandingPages = [selectedPage, ...this.existingLandingPages]
      }
    } finally {
      this.captureSavedState()
      this.loading = false
    }
  },
  mounted () {
    window.addEventListener('discard-unsaved-changes', this.onDiscardUnsavedChanges)
  },
  beforeDestroy () {
    window.removeEventListener('discard-unsaved-changes', this.onDiscardUnsavedChanges)
  }
}
</script>

<style lang="sass" scoped>

.hint
  font-size: 13px
  color: #767676

.loading-ctn
  display: flex
  justify-content: center
  padding: 48px 0

.layout-cards
  display: flex
  gap: 16px
  max-width: 700px

.layout-cards > *
  flex: 1

.section-label
  font-size: 11px
  font-weight: 500
  letter-spacing: 1px
  color: #767676
  text-transform: uppercase

.existing-page-select
  max-width: 1056px

.page-tag
  font-size: 12px
  color: #999
  margin-left: 12px
  flex-shrink: 0

.borrowed-note
  display: flex
  align-items: center
  margin-top: 8px
  font-size: 12px
  color: #767676

.form-input
  max-width: 1056px

.redirection-details
  display: flex
  flex-direction: column
  height: 100%
  min-height: 0

.redirection-content
  flex: 1 1 auto
  overflow-y: auto
  padding-bottom: 8px
  min-height: 0

.actions-ctn
  position: sticky
  bottom: 0
  background: white
  padding: 16px 24px
  display: flex
  align-items: center
  gap: 12px
  border-top: 1px solid #eee
  z-index: 5

.save-btn
  margin: 0
  min-width: 140px

.save-hint
  display: inline-flex
  align-items: center
  font-size: 12px
  color: #767676

@media (max-width: 960px)
  .actions-ctn
    position: fixed
    left: 0
    right: 0
    bottom: 0
    padding: 12px 16px
    z-index: 20
  .redirection-content
    padding-bottom: 80px

</style>
